import React, { useState, useEffect, useRef } from 'react';
import chatService from '../services/chatService';

const GlobalNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [currentNotificationIndex, setCurrentNotificationIndex] = useState(0);
  const autoCloseTimerRef = useRef(null);
  const progressTimerRef = useRef(null);

  useEffect(() => {
    // Подписываемся на глобальные уведомления
    const unsubscribe = chatService.subscribeToGlobalNotifications((newNotifications) => {
      setNotifications(newNotifications);
      
      // Показываем компонент, если есть новые уведомления
      if (newNotifications.length > 0) {
        setIsVisible(true);
        setCurrentNotificationIndex(0);
        
        // Записываем статистику просмотра в Firebase
        const currentNotification = newNotifications[0];
        chatService.recordNotificationView(currentNotification.id, currentNotification.orderNumber);
        
        // Отмечаем уведомление как показанное
        chatService.markNotificationAsShown(currentNotification.id);
        
        // Запускаем автозакрытие
        startAutoClose();
      }
    });

    return () => {
      unsubscribe();
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startAutoClose = () => {
    clearTimers();
    
    // Автоматически скрываем через 6 секунд
    autoCloseTimerRef.current = setTimeout(() => {
      handleClose();
    }, 6000);
    
    // Обновляем прогресс-бар каждые 100мс
    let progress = 0;
    progressTimerRef.current = setInterval(() => {
      progress += 100 / 60; // 6000ms / 60 updates = 100ms per update
      if (progress >= 100) {
        clearInterval(progressTimerRef.current);
      }
    }, 100);
  };

  const clearTimers = () => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const handleClose = () => {
    clearTimers();
    setIsVisible(false);
    
    // Очищаем уведомления после закрытия
    setTimeout(() => {
      setNotifications([]);
      setCurrentNotificationIndex(0);
    }, 300);
  };

  const handleNextNotification = () => {
    if (currentNotificationIndex < notifications.length - 1) {
      const nextIndex = currentNotificationIndex + 1;
      setCurrentNotificationIndex(nextIndex);
      
      // Записываем статистику для следующего уведомления
      const nextNotification = notifications[nextIndex];
      chatService.recordNotificationView(nextNotification.id, nextNotification.orderNumber);
      chatService.markNotificationAsShown(nextNotification.id);
      
      // Перезапускаем таймер
      startAutoClose();
    } else {
      handleClose();
    }
  };

  if (!isVisible || notifications.length === 0) return null;

  const currentNotification = notifications[currentNotificationIndex];
  
  // Проверяем, что уведомление существует
  if (!currentNotification) return null;

  // Функция для скрытия номера заказа
  const hideOrderNumber = (orderNumber) => {
    if (!orderNumber || orderNumber.length < 6) return orderNumber;
    
    const start = orderNumber.substring(0, 3);
    const end = orderNumber.substring(orderNumber.length - 2);
    const hidden = '*'.repeat(orderNumber.length - 5);
    
    return `${start}${hidden}${end}`;
  };

  return (
    <div className="fixed top-4 left-4 z-50 max-w-sm">
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg shadow-2xl p-4 transform transition-all duration-500 animate-slide-in-left">
        {/* Декоративные элементы */}
        <div className="absolute top-0 right-0 w-12 h-12 bg-white opacity-10 rounded-full -translate-y-6 translate-x-6"></div>
        <div className="absolute bottom-0 left-0 w-8 h-8 bg-white opacity-10 rounded-full translate-y-4 -translate-x-4"></div>
        
        <div className="relative z-10">
          {/* Заголовок */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white animate-bounce" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-white">🎉 Кто-то обновился!</h3>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Текущее уведомление */}
          <div className="bg-white bg-opacity-20 rounded-md p-3 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
              <span className="text-sm text-green-100">
                <span className="font-semibold text-white">Заказ #{hideOrderNumber(currentNotification.orderNumber)}</span> восстановлен!
              </span>
            </div>
            <div className="text-xs text-green-200">
              Старый ключ заменен на премиум подписку 🚀
            </div>
          </div>

          {/* Навигация по уведомлениям */}
          {notifications.length > 1 && (
            <div className="flex items-center justify-between mb-3">
              <div className="flex gap-1">
                {notifications.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full ${
                      index === currentNotificationIndex 
                        ? 'bg-white' 
                        : 'bg-white bg-opacity-30'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={handleNextNotification}
                className="text-xs text-green-200 hover:text-white transition-colors"
              >
                {currentNotificationIndex < notifications.length - 1 ? 'Следующее →' : 'Закрыть'}
              </button>
            </div>
          )}

          {/* Дополнительная информация */}
          <div className="text-xs text-green-200 text-center mb-2">
            💡 Присоединяйтесь! Восстановите свой ключ прямо сейчас
          </div>

          {/* Счетчик активности */}
          <div className="text-xs text-green-200 text-center">
            {notifications.length > 1 && (
              <span>{currentNotificationIndex + 1} из {notifications.length} уведомлений</span>
            )}
          </div>
        </div>

        {/* Анимированная полоска прогресса */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white bg-opacity-20 rounded-b-lg overflow-hidden">
          <div className="h-full bg-white bg-opacity-40 animate-progress-bar"></div>
        </div>
      </div>
    </div>
  );
};

export default GlobalNotifications;
